import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AcknowledgeDialogComponent } from '../acknowledge-dialog/acknowledge-dialog.component';
import { DownloaderService } from '../../services/downloader.service';
import { MountpointConnectionService, MountPointConnection, PagedResult } from '../../services/mountpoint-connection.service';
import { PermissionService } from '../../services/permission.service';

interface DownloaderInputs {
  durationSec: number;
  planStartAt: string;
  scheduleLeadMin: number;
}

@Component({
  selector: 'app-ppk-downloader-pro',
  templateUrl: './ppk-downloader-pro.component.html',
  styleUrls: ['./ppk-downloader-pro.component.scss']
})
export class PpkDownloaderProComponent implements OnInit, OnDestroy {
  title = 'PPK Downloader (Pro Edition)';
  search = '';
  isLoading = false;
  errorMessage = '';

  connections: MountPointConnection[] = [];
  sortedConnections: MountPointConnection[] = [];

  currentPage = 1;
  readonly pageSize = 1;

  selectedConnection?: MountPointConnection;
  isDemo = false;

  readonly durationPresetOptions: { minutes: number; label: string }[] = [
    { minutes: 5, label: '5 minutes' },
    { minutes: 10, label: '10 minutes' },
    { minutes: 15, label: '15 minutes' },
    { minutes: 30, label: '30 minutes' },
    { minutes: 45, label: '45 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 90, label: '1 hour 30 minutes' },
    { minutes: 120, label: '2 hours' },
    { minutes: 180, label: '3 hours' },
    { minutes: 240, label: '4 hours' },
    { minutes: 300, label: '5 hours' },
    { minutes: 360, label: '6 hours' },
    { minutes: 420, label: '7 hours' },
    { minutes: 480, label: '8 hours' },
    { minutes: 540, label: '9 hours' },
    { minutes: 600, label: '10 hours' },
    { minutes: 720, label: '12 hours' },
    { minutes: 840, label: '14 hours' },
    { minutes: 960, label: '16 hours' },
    { minutes: 1200, label: '20 hours' },
    { minutes: 1320, label: '22 hours' },
    { minutes: 1440, label: '24 hours (max)' }
  ];

  private downloaderInputsByKey: { [key: string]: DownloaderInputs } = {};
  private downloaderLoadingByMountpoint: { [mp: string]: boolean } = {};
  downloaderUrlByMountpoint: { [mp: string]: { url: string; expiresAt: string; lastModified?: string; running?: boolean } | null } = {};

  constructor(
    private readonly connectionsService: MountpointConnectionService,
    private readonly downloaderService: DownloaderService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly perms: PermissionService
  ) {
    this.perms.ensureFresh();
    this.isDemo = this.perms.has('demo');
  }

  ngOnInit(): void {
    this.loadConnections();
  }

  ngOnDestroy(): void {
  }

  // ---------- Data Loading ----------

  loadConnections(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.connections = [];
    this.sortedConnections = [];
    const pageSize = 100;
    this.fetchPage(0, pageSize, [], (all) => {
      this.connections = all;
      this.sortedConnections = [...this.connections].sort((a, b) => (a.mountPoint || '').localeCompare(b.mountPoint || ''));
      this.currentPage = 1;
      this.selectedConnection = this.currentConnection;
      if (this.selectedConnection) {
        this.ensureInputsFor(this.selectedConnection);
        this.refreshStatus(this.selectedConnection);
      }
      this.isLoading = false;
    });
  }

  private fetchPage(page: number, size: number, acc: MountPointConnection[], done: (all: MountPointConnection[]) => void): void {
    this.connectionsService.list(this.search, page, size).subscribe({
      next: (res: PagedResult<MountPointConnection>) => {
        const nextAcc = acc.concat(res.content || []);
        const totalPages = res.totalPages ?? 0;
        if (page + 1 < totalPages) {
          this.fetchPage(page + 1, size, nextAcc, done);
        } else {
          done(nextAcc);
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load mount point connections.';
        this.snackBar.open('Failed to load mount point connections.', 'Close', { duration: 4000 });
      }
    });
  }

  onSearch(): void {
    this.loadConnections();
  }

  get hasConnections(): boolean {
    return this.sortedConnections.length > 0;
  }

  get totalPages(): number {
    if (!this.hasConnections) return 0;
    return Math.ceil(this.sortedConnections.length / this.pageSize);
  }

  get currentConnection(): MountPointConnection | undefined {
    if (!this.hasConnections) return undefined;
    const index = (this.currentPage - 1) * this.pageSize;
    return this.sortedConnections[index];
  }

  selectFromDropdown(connectionKey: string): void {
    if (!connectionKey) return;
    const idx = this.sortedConnections.findIndex(c => this.getConnectionKey(c) === connectionKey);
    if (idx >= 0) {
      const page = Math.floor(idx / this.pageSize) + 1;
      this.goToPage(page);
    }
  }

  goToConnection(conn: MountPointConnection): void {
    if (!conn) return;
    const key = this.getConnectionKey(conn);
    const idx = this.sortedConnections.findIndex(c => this.getConnectionKey(c) === key);
    if (idx >= 0) {
      const page = Math.floor(idx / this.pageSize) + 1;
      this.goToPage(page);
    }
  }

  goFirst(): void { this.goToPage(1); }
  goLast(): void { if (this.totalPages > 0) { this.goToPage(this.totalPages); } }

  goPrev(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goNext(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPage(page: number): void {
    if (this.totalPages === 0) {
      this.currentPage = 1;
      this.selectedConnection = undefined;
      return;
    }
    const clamped = Math.min(Math.max(1, page), this.totalPages);
    this.currentPage = clamped;
    this.selectedConnection = this.currentConnection;
    if (this.selectedConnection) {
      this.ensureInputsFor(this.selectedConnection);
      this.refreshStatus(this.selectedConnection);
    }
  }

  get pageWindow(): number[] {
    const total = this.totalPages;
    if (total <= 0) return [];
    const targetCount = Math.min(5, total);
    const windowRadius = Math.floor(targetCount / 2);
    let start = Math.max(1, this.currentPage - windowRadius);
    let end = Math.min(total, start + targetCount - 1);
    start = Math.max(1, Math.min(start, end - targetCount + 1));
    const pages: number[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  }

  // ---------- Downloader helpers ----------

  downInputs(conn: MountPointConnection): DownloaderInputs {
    this.ensureInputsFor(conn);
    const key = this.getConnectionKey(conn);
    return this.downloaderInputsByKey[key];
  }

  private ensureInputsFor(conn: MountPointConnection): void {
    const key = this.getConnectionKey(conn);
    if (!this.downloaderInputsByKey[key]) {
      this.downloaderInputsByKey[key] = {
        durationSec: 120,
        planStartAt: this.nowIsoWithOffset(),
        scheduleLeadMin: 1
      };
    }
  }

  getDurationSelection(conn: MountPointConnection): number {
    const inputs = this.downInputs(conn);
    const mins = Math.max(5, Math.min(1440, Math.round(inputs.durationSec / 60 / 5) * 5));
    return mins;
  }

  onDurationSelectionChange(conn: MountPointConnection, minutes: number): void {
    const clamped = Math.max(5, Math.min(1440, Number(minutes) || 5));
    this.downInputs(conn).durationSec = clamped * 60;
  }

  getPlanStartAtInput(conn: MountPointConnection): string | null {
    const iso = this.downInputs(conn).planStartAt;
    return this.isoToLocalInputValue(iso);
  }

  onPlanStartAtInputChange(conn: MountPointConnection, val: string): void {
    const iso = this.localInputValueToIsoWithOffset(val) || this.nowIsoWithOffset();
    this.downInputs(conn).planStartAt = iso;
  }

  isSubmitting(conn: MountPointConnection): boolean {
    const key = conn.mountPoint || '';
    return this.downloaderLoadingByMountpoint[key] === true;
  }

  onSubmitRecording(conn: MountPointConnection): void {
    if (this.isDemo) {
      this.readOnlyNotice();
      return;
    }
    if (!this.perms.has('downloader-pro')) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'You do not have permission to start downloader.' });
      return;
    }
    const mountpoint = conn.mountPoint;
    const userId = conn.userId;
    const serverHost = conn.server?.trim();
    const port = conn.port?.trim();
    if (!mountpoint || !userId || !serverHost || !port) {
      this.snackBar.open('Mount point connection is missing required fields.', 'Close', { duration: 3000 });
      return;
    }
    if (!this.isPortNumeric(port)) {
      this.snackBar.open('Port must be numeric (1-65535).', 'Close', { duration: 3000 });
      return;
    }
    const inputs = this.downInputs(conn);
    const scheduleLeadMin = inputs.scheduleLeadMin ?? 1;
    const extras = {
      userId,
      server: `${serverHost}:${port}`,
      username: conn.downloaderUserName,
      password: conn.downloaderPassword
    };
    this.downloaderLoadingByMountpoint[mountpoint] = true;
    this.downloaderService.start(mountpoint, inputs.durationSec, inputs.planStartAt, scheduleLeadMin, extras).subscribe({
      next: (res) => {
        this.downloaderLoadingByMountpoint[mountpoint] = false;
        const body: any = (res as any)?.body ?? res;
        const msg = body?.message || (body?.accepted ? 'Recorder triggered (accepted).' : 'Downloader request processed.');
        const friendly = /idempotency window|already running|within idempotency/i.test(msg)
          ? `Recorder already running or scheduled to be run for ${mountpoint}`
          : msg;
        this.dialog.open(AcknowledgeDialogComponent, { data: friendly });
        this.downloaderUrlByMountpoint[mountpoint] = { running: true } as any;
      },
      error: (err) => {
        this.downloaderLoadingByMountpoint[mountpoint] = false;
        const status = err?.status;
        const raw = err?.error?.error || err.message || 'Unknown Error';
        const friendly = status === 0
          ? 'Network error: backend unreachable or request aborted. Please try again.'
          : `Failed to start downloader: ${raw}`;
        this.dialog.open(AcknowledgeDialogComponent, { data: friendly });
      }
    });
  }

  onRefreshStatus(conn: MountPointConnection): void {
    this.refreshStatus(conn);
  }

  private refreshStatus(conn: MountPointConnection): void {
    const mountpoint = conn.mountPoint;
    if (!mountpoint) {
      return;
    }
    this.downloaderService.getDownloaderUrl(mountpoint).subscribe({
      next: (out) => {
        this.downloaderUrlByMountpoint[mountpoint] = out as any;
      },
      error: () => {
        this.downloaderUrlByMountpoint[mountpoint] = null;
      }
    });
  }

  // ---------- Date helpers ----------

  getExpiryDate(dl: { lastModified?: string } | null | undefined): Date | null {
    try {
      const iso = dl?.lastModified;
      if (!iso || typeof iso !== 'string') return null;
      const created = new Date(iso);
      if (isNaN(created.getTime())) return null;
      const expMs = created.getTime() + 7 * 24 * 60 * 60 * 1000;
      return new Date(expMs);
    } catch {
      return null;
    }
  }

  isExpired(dl: { lastModified?: string } | null | undefined): boolean {
    const exp = this.getExpiryDate(dl);
    if (!exp) return false;
    return Date.now() > exp.getTime();
  }

  // ---------- Utility helpers ----------

  isPortNumeric(value?: string | null): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (!/^\d+$/.test(trimmed)) return false;
    const num = Number(trimmed);
    return num >= 1 && num <= 65535;
  }

  private pad(n: number): string { return n < 10 ? '0' + n : String(n); }

  private nowIsoWithOffset(): string {
    const d = new Date();
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? '+' : '-';
    const abs = Math.abs(tz);
    const hh = this.pad(Math.floor(abs / 60));
    const mm = this.pad(abs % 60);
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}:${this.pad(d.getSeconds())}${sign}${hh}:${mm}`;
  }

  private isoToLocalInputValue(iso?: string): string | null {
    if (!iso || typeof iso !== 'string') return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
  }

  private localInputValueToIsoWithOffset(val?: string): string | null {
    if (!val || typeof val !== 'string') return null;
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const hour = Number(m[4]);
    const minute = Number(m[5]);
    const d = new Date(year, month - 1, day, hour, minute, 0);
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? '+' : '-';
    const abs = Math.abs(tz);
    const hh = this.pad(Math.floor(abs / 60));
    const mm = this.pad(abs % 60);
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}:${this.pad(d.getSeconds())}${sign}${hh}:${mm}`;
  }

  getConnectionKey(conn?: MountPointConnection | null): string {
    if (!conn) return '';
    if (conn.mountPointId != null) {
      return `id:${conn.mountPointId}`;
    }
    return `mp:${conn.mountPoint || ''}`;
  }

  private readOnlyNotice(): void {
    this.dialog.open(AcknowledgeDialogComponent, { data: 'You have read-only access, but not permission to make changes.' });
  }
}


